/**
 * WS 下行 data 段归一化：兼容顶层及 stockInfo 项内 JSON 字符串字段（如 stockDetail）。
 *
 * @module utils/realtime/normalizePushData
 */

import { isArray, isNil, isPlainObject, isString } from 'lodash-es';
import { parseJsonValue } from '@/utils/json';

/**
 * 解析 `stockDetail` 字段：支持 JSON 字符串或已解析对象。
 * @param {unknown} stockDetail
 * @returns {Record<string, unknown>}
 */
export function normalizeStockDetail(stockDetail) {
  if (isNil(stockDetail) || stockDetail === '') {
    return {};
  }
  if (isPlainObject(stockDetail)) {
    return { ...stockDetail };
  }
  const parsed = parseJsonValue(stockDetail, { warnLabel: 'stockDetail' });
  return isPlainObject(parsed) ? { ...parsed } : {};
}

/**
 * 归一化单条 stockInfo 明细：将 `stockDetail` 从 JSON 字符串转为对象。
 * @param {unknown} item
 * @returns {unknown}
 */
function normalizeStockInfoItem(item) {
  if (!isPlainObject(item) || !Object.prototype.hasOwnProperty.call(item, 'stockDetail')) {
    return item;
  }
  const raw = item.stockDetail;
  // 已是对象则不在此层克隆，由业务 parsePush 按需展开
  if (!isString(raw)) {
    return item;
  }
  return {
    ...item,
    stockDetail: normalizeStockDetail(raw)
  };
}

/**
 * 归一化已解析的 data 结构（stockInfo / list / 单条 / 根级 stockDetail）。
 * @param {unknown} data
 * @returns {unknown}
 */
function normalizePushDataShape(data) {
  if (isNil(data)) {
    return data;
  }
  if (isArray(data)) {
    return data.map((item) => normalizeStockInfoItem(item));
  }
  if (!isPlainObject(data)) {
    return data;
  }

  let result = data;

  if (Object.prototype.hasOwnProperty.call(result, 'stockDetail') && isString(result.stockDetail)) {
    result = {
      ...result,
      stockDetail: normalizeStockDetail(result.stockDetail)
    };
  }

  if (isArray(result.stockInfo)) {
    const stockInfo = result.stockInfo.map((item) => normalizeStockInfoItem(item));
    if (stockInfo !== result.stockInfo) {
      result = { ...result, stockInfo };
    }
  }

  if (isArray(result.list)) {
    const list = result.list.map((item) => normalizeStockInfoItem(item));
    if (list !== result.list) {
      result = { ...result, list };
    }
  }

  if ((result.fullCode || result.stockCode) && Object.prototype.hasOwnProperty.call(result, 'stockDetail')) {
    return normalizeStockInfoItem(result);
  }

  return result;
}

/**
 * 兼容 hub 下行 `data` 为 JSON 字符串，并解析其中 `stockDetail` 等嵌套 JSON 字符串字段。
 * @param {unknown} data hub 回调的 data 段
 * @returns {unknown} 归一化后的 data
 */
export function normalizePushData(data) {
  const top = parseJsonValue(data, { warnLabel: 'push data' });
  return normalizePushDataShape(top);
}
